import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "../components/tag/Layout";
import { api } from "../lib/api";

const fields = [
  { key: "name", label: "Full name" },
  { key: "website", label: "Website URL" },
  { key: "industry", label: "Industry or niche" },
  { key: "niche", label: "Specific sub-niche or audience" },
  { key: "revenue_range", label: "Current annual revenue range (e.g. $0-100k, $100k-500k)" },
  { key: "current_funnel", label: "Describe your current funnel (traffic source → offer → conversion)", multiline: true },
  { key: "offer", label: "Primary offer or service" },
  { key: "frustration", label: "Biggest growth bottleneck right now", multiline: true },
  { key: "success_vision", label: "What a successful 90 days looks like", multiline: true },
  { key: "notes", label: "Anything else we should know", multiline: true, optional: true },
];

export default function BlueprintIntake() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    email: params.get("email") || "",
    name: "",
    website: "",
    industry: "",
    niche: "",
    revenue_range: "",
    current_funnel: "",
    offer: "",
    frustration: "",
    success_vision: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    for (const f of fields) {
      if (!f.optional && !form[f.key].trim()) {
        toast.error(`Please fill: ${f.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post("/blueprint/intake", form);
      setDone(true);
    } catch {
      toast.error("Could not submit intake. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout title="Blueprint Intake | The Authority Gap" path="/blueprint/intake">
      <section style={{ background: "#090909", padding: "60px 20px 80px" }}>
        <div className="tag-card" data-testid="blueprint-intake-card" style={{ maxWidth: 580, margin: "0 auto", padding: 32 }}>
          {!done ? (
            <>
              <h2 className="tag-h2" style={{ marginBottom: 8 }}>Your Authority Blueprint Intake</h2>
              <p style={{ color: "#aaa", fontSize: 13, marginBottom: 24 }}>
                We use this to generate your personalized 90-day plan. Most customers receive their PDF within minutes of submitting.
              </p>
              <form onSubmit={submit}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", color: "#888", fontSize: 11, marginBottom: 6, fontWeight: 600 }}>Email</label>
                  <input data-testid="bp-intake-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="tag-input" />
                </div>
                {fields.map((f) => (
                  <div key={f.key} style={{ marginBottom: 14 }}>
                    <label style={{ display: "block", color: "#888", fontSize: 11, marginBottom: 6, fontWeight: 600 }}>{f.label}{f.optional ? " (optional)" : ""}</label>
                    {f.multiline ? (
                      <textarea
                        data-testid={`bp-intake-${f.key}`}
                        rows={3}
                        value={form[f.key]}
                        onChange={(e) => set(f.key, e.target.value)}
                        className="tag-input"
                        style={{ resize: "vertical", minHeight: 80, fontFamily: "var(--font-body)" }}
                      />
                    ) : (
                      <input
                        data-testid={`bp-intake-${f.key}`}
                        value={form[f.key]}
                        onChange={(e) => set(f.key, e.target.value)}
                        className="tag-input"
                      />
                    )}
                  </div>
                ))}
                <button data-testid="bp-intake-submit" type="submit" className="btn-primary" style={{ width: "100%" }} disabled={submitting}>
                  {submitting ? "Submitting…" : "Generate My Blueprint"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="bp-intake-success" style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ color: "#FF6575", fontSize: 56, marginBottom: 12 }}>✓</div>
              <h2 className="tag-h2" style={{ marginBottom: 12 }}>Generating your Blueprint…</h2>
              <p style={{ color: "#bbb", fontSize: 13, lineHeight: 1.7 }}>
                We&apos;re building your branded 90-day Authority Blueprint right now. You&apos;ll get an email with the download link in the next few minutes. Check spam if you don&apos;t see it.
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
